import { groupBy } from 'lodash';
import { ApiError, createAction } from 'redux-api-middleware';
import { RemoteImage } from '../../common/Types';
import { endpointWithUserId } from '../../common/url';
import { State as StoreState } from '../types/State';
import { Dispatch, GetState, ThunkAction } from '../types/Store';
import { ActivityRedemptionDetail, DirectRewardRequest } from './ActivityRedemption';
import {
  HTTP_CONTENT_TYPE_HEADER,
  HTTP_CONTENT_TYPE_JSON,
  HTTP_DELETE,
  HTTP_GET,
  HTTP_PUT,
} from './ApiAction';

/*
 This reducer loads and maintains the state for the Bonus-Activities provided by BE.
 */
export const LOAD_BONUS_ACTIVITIES = 'barmer-bonus-shared/activities/bonus/LOAD' as const;
export const LOAD_BONUS_ACTIVITIES_SUCCESS = 'barmer-bonus-shared/activities/bonus/SUCCESS' as const;
export const LOAD_BONUS_ACTIVITIES_FAILURE = 'barmer-bonus-shared/activities/bonus/FAILURE' as const;

export const RESET_BONUS_ACTIVITIES = 'barmer-bonus-shared/activities/bonus/RESET' as const;

export const DISMISS_PROMINENT_BOOKLETS = 'barmer-bonus-shared/activities/bonus/dismissProminentBooklets' as const;
export const DISMISS_PROMINENT_BOOKLETS_SUCCESS = 'barmer-bonus-shared/activities/bonus/dismissProminentBooklets/SUCCESS' as const;
export const DISMISS_PROMINENT_BOOKLETS_ERROR = 'barmer-bonus-shared/activities/bonus/dismissProminentBooklets/ERROR' as const;

export const DELETE_BONUS_ACTIVITY = 'barmer-bonus-shared/activities/bonus/DELETE' as const;
export const DELETE_BONUS_ACTIVITY_SUCCESS = 'barmer-bonus-shared/activities/bonus/DELETE_SUCCESS' as const;
export const DELETE_BONUS_ACTIVITY_FAILURE = 'barmer-bonus-shared/activities/bonus/DELETE_FAILURE' as const;
export const DELETE_BONUS_ACTIVITY_RESET = 'barmer-bonus-shared/activities/bonus/DELETE_RESET' as const;

export const LOAD_BONUS_ACTIVITY_ATTACHMENT = 'barmer-bonus-shared/activities/bonus/attachments/LOAD' as const;
export const LOAD_BONUS_ACTIVITY_ATTACHMENT_SUCCESS = 'barmer-bonus-shared/activities/bonus/attachments/SUCCESS' as const;
export const LOAD_BONUS_ACTIVITY_ATTACHMENT_FAILURE = 'barmer-bonus-shared/activities/bonus/attachments/FAILURE' as const;

type LoadActivities = {
  type: typeof LOAD_BONUS_ACTIVITIES;
  payload?: ApiError;
  error?: boolean;
};

type SuccessActivities = {
  type: typeof LOAD_BONUS_ACTIVITIES_SUCCESS;
  payload: { bonus: Array<BonusActivity>; booklets: Array<BonusBooklet> };
};

type FailureActivities = {
  type: typeof LOAD_BONUS_ACTIVITIES_FAILURE;
  payload: ApiError;
  error: boolean;
};

type DismissProminentBookletActivity = {
  type: typeof DISMISS_PROMINENT_BOOKLETS;
  payload: { bookletIds: Array<string> };
};

type DismissProminentBookletActivitySuccess = {
  type: typeof DISMISS_PROMINENT_BOOKLETS_SUCCESS;
};

type DismissProminentBookletActivityFailure = {
  type: typeof DISMISS_PROMINENT_BOOKLETS_ERROR;
  payload: ApiError;
  error: boolean;
};

type ResetActivities = {
  type: typeof RESET_BONUS_ACTIVITIES;
};

type LoadBonusAttachments = {
  type: typeof LOAD_BONUS_ACTIVITY_ATTACHMENT;
};

type LoadBonusAttachmentsSuccess = {
  type: typeof LOAD_BONUS_ACTIVITY_ATTACHMENT_SUCCESS;
  payload: {
    attachments: Array<Attachment>;
    activityId: number;
    redemptionRequestId: number;
    empty: boolean;
  };
};

type LoadBonusAttachmentsFailure = {
  type: typeof LOAD_BONUS_ACTIVITY_ATTACHMENT_FAILURE;
};

type DeleteActivityAction = { type: typeof DELETE_BONUS_ACTIVITY };
type DeleteActivitySuccessAction = { type: typeof DELETE_BONUS_ACTIVITY_SUCCESS };
type DeleteActivityFailureAction = {
  type: typeof DELETE_BONUS_ACTIVITY_FAILURE;
  payload: ApiError;
};
type DeleteActivityResetAction = { type: typeof DELETE_BONUS_ACTIVITY_RESET };

export type Action =
  | LoadActivities
  | SuccessActivities
  | FailureActivities
  | DismissProminentBookletActivity
  | DismissProminentBookletActivitySuccess
  | DismissProminentBookletActivityFailure
  | ResetActivities
  | LoadBonusAttachments
  | LoadBonusAttachmentsSuccess
  | LoadBonusAttachmentsFailure
  | DeleteActivityAction
  | DeleteActivitySuccessAction
  | DeleteActivityFailureAction
  | DeleteActivityResetAction;

export const SUBMISSION_OPEN = 'open' as const;
export const SUBMISSION_IN_REVIEW = 'in-review' as const;
export const SUBMISSION_LOCKED = 'locked' as const;
export const SUBMISSION_ACCEPTED = 'accepted' as const;
export const SUBMISSION_REJECTED = 'rejected' as const;

export const PROMINENT_BONUS_BOOKLET = 'prominent-bonus-booklet' as const;
export const BONUS_BOOKLET = 'bonus-booklet' as const;

export type BONUS_BOOKLET_TYPE = typeof PROMINENT_BONUS_BOOKLET | typeof BONUS_BOOKLET;

export type SubmissionState =
  | typeof SUBMISSION_OPEN
  | typeof SUBMISSION_IN_REVIEW
  | typeof SUBMISSION_LOCKED
  | typeof SUBMISSION_ACCEPTED
  | typeof SUBMISSION_REJECTED;

export const BONUS_CATEGORY_SPORTS = 'sports' as const;
export const BONUS_CATEGORY_COURSE = 'course' as const;
export const BONUS_CATEGORY_MEDICAL = 'medical' as const;

export type Category =
  | typeof BONUS_CATEGORY_SPORTS
  | typeof BONUS_CATEGORY_COURSE
  | typeof BONUS_CATEGORY_MEDICAL;

export type ExternalLink = {
  title: string;
  href: string;
};

export const LIST_TYPE_ORDERED = 'ordered' as const;
export const LIST_TYPE_NUMBERED = 'numbered' as const;

export type ListType = {
  type: typeof LIST_TYPE_ORDERED | typeof LIST_TYPE_NUMBERED;
  items: Array<string>;
};

export type DescriptionBlock = {
  text?: string;
  list?: ListType;
  links?: Array<ExternalLink>;
};

export type Attachment = {
  full: RemoteImage;
  thumbnail: RemoteImage;
  imagePointer: number;
};

export type BonusBooklet = {
  bookletId: string;
  year: number;
  type: BONUS_BOOKLET_TYPE;
  headerBackgroundColor: string;
  headerImageApp: string;
  headerImageWeb: string;
  title: string;
  descriptionOfWhat?: DescriptionBlock;
  descriptionOfHow?: DescriptionBlock;
  extLinks?: Array<ExternalLink>;
};

export type BonusActivity = {
  id: number;
  year: number;
  headerBackgroundColor: string;
  headerImageApp: string;
  headerImageWeb: string;
  title: string;
  subtitle: string;
  ageLabel?: string;
  descriptionOfWhat?: DescriptionBlock;
  descriptionOfHow?: DescriptionBlock;
  extLinks?: Array<ExternalLink>;
  points: number;
  state: SubmissionState;
  submissionDate: string;
  rejectionReason?: string;
  category: Category;
  catalogue: string;
  frequency: string;
  key: string;
  attachments?: Array<Attachment>;
  redemptionRequestId?: number;
  group?: string;
  valueInEUR?: number;
  ensureRedeemableActivityInChequebook: boolean;
  directRewardEnabled: boolean;
  directRewardChosen: boolean;
  directRewardRequest?: DirectRewardRequest;
  multipleRedeemableActivityDetail?: ActivityRedemptionDetail;
};

export type BonusActivityGroup = {
  year: number;
  title: string;
  maxPoints: number;
  maxEUR?: number;
  activities: BonusActivity[];
};

export type GenericBonusActivityGroup<T extends string | number> = {
  [key in T]: Array<BonusActivity>;
};

export type State = {
  loading: boolean;
  attachmentsLoading: boolean;
  deleting: boolean;
  deleted: boolean;
  booklets: Array<BonusBooklet>;
  bonus: Array<BonusActivity>;
  bonusGroups: BonusActivityGroup[];
  error?: ApiError;
};

const initialState: State = {
  loading: false,
  attachmentsLoading: false,
  deleting: false,
  deleted: false,
  booklets: [],
  bonus: [],
  bonusGroups: [],
  error: undefined,
};

export function getBonusActivityGroups(bonus: BonusActivity[]) {
  const result: BonusActivityGroup[] = [];

  const groupedByType = groupBy(
    bonus.filter((x) => x.group !== null && x.state === SUBMISSION_OPEN),
    (x) => x.group,
  );

  Object.keys(groupedByType).forEach((type) => {
    const groupedByYear = groupBy(groupedByType[type], (x) => x.year);
    Object.keys(groupedByYear).forEach((year) => {
      const maxEUR = Math.max(...groupedByYear[year].map((a) => a.valueInEUR ?? 0));

      result.push({
        title: type,
        year: parseInt(year, 10),
        maxPoints: Math.max(...groupedByYear[year].map((a) => a.points)),
        maxEUR: maxEUR === 0 ? undefined : maxEUR,
        activities: groupedByYear[year],
      });
    });
  });
  return result;
}

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case LOAD_BONUS_ACTIVITIES:
      return {
        ...state,
        loading: action.error === undefined,
        error: action.error ? action.payload : undefined,
      };

    case LOAD_BONUS_ACTIVITIES_SUCCESS:
      return {
        ...state,
        loading: false,
        bonus: action.payload.bonus,
        bonusGroups: getBonusActivityGroups(action.payload.bonus),
        booklets: action.payload.booklets,
        error: undefined,
      };

    case LOAD_BONUS_ACTIVITIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case DISMISS_PROMINENT_BOOKLETS:
      const dismissAction: DismissProminentBookletActivity = action; // eslint-disable-line no-case-declarations
      return {
        ...state,
        booklets: state.booklets.map((booklet: BonusBooklet) => {
          if (dismissAction.payload.bookletIds.find((id: string) => id === booklet.bookletId)) {
            return {
              ...booklet,
              type: BONUS_BOOKLET,
            };
          }
          return booklet;
        }),
      };

    case DISMISS_PROMINENT_BOOKLETS_SUCCESS:
    case DISMISS_PROMINENT_BOOKLETS_ERROR:
      return {
        ...state,
      };

    case LOAD_BONUS_ACTIVITY_ATTACHMENT:
      return {
        ...state,
        attachmentsLoading: true,
      };

    case LOAD_BONUS_ACTIVITY_ATTACHMENT_FAILURE:
      return {
        ...state,
        attachmentsLoading: false,
      };

    case LOAD_BONUS_ACTIVITY_ATTACHMENT_SUCCESS:
      const successAction: LoadBonusAttachmentsSuccess = action; // eslint-disable-line no-case-declarations
      const { attachments, activityId, redemptionRequestId } = successAction.payload; // eslint-disable-line no-case-declarations
      return {
        ...state,
        attachmentsLoading: false,
        bonus: state.bonus.map((activity: BonusActivity) => {
          if (activity.id === activityId) {
            return {
              ...activity,
              attachments: attachments.length > 0 ? attachments : undefined,
              redemptionRequestId,
            };
          }
          return activity;
        }),
      };

    case RESET_BONUS_ACTIVITIES:
      return initialState;

    case DELETE_BONUS_ACTIVITY:
      return {
        ...state,
        deleting: true,
      };

    case DELETE_BONUS_ACTIVITY_SUCCESS:
      return {
        ...state,
        deleting: false,
        deleted: true,
      };

    case DELETE_BONUS_ACTIVITY_FAILURE:
      return {
        ...state,
        deleting: false,
        error: action.payload,
      };

    case DELETE_BONUS_ACTIVITY_RESET:
      return {
        ...state,
        deleting: false,
        deleted: false,
        error: undefined,
      };

    default:
      return state;
  }
}

// Whether the activity list has been loaded
export function isLoaded(globalState: StoreState): boolean {
  return globalState.bonusActivity.bonus.length > 0;
}

// Whether the activity list is being loaded
export function isLoading(globalState: StoreState): boolean {
  return globalState.bonusActivity.loading;
}

export function groupBonusActivitiesByYear(
  bonus: Array<BonusActivity>,
): GenericBonusActivityGroup<number> {
  return groupBy(bonus, (x) => x.year);
}

export function groupBonusActivitiesByState(
  bonus: Array<BonusActivity>,
): GenericBonusActivityGroup<SubmissionState> {
  return groupBy(bonus, (x) => x.state) as GenericBonusActivityGroup<SubmissionState>;
}

export function isActivityEditable(state: SubmissionState): boolean {
  return state === SUBMISSION_REJECTED || state === SUBMISSION_IN_REVIEW;
}

export function getUngroupedBonusActivities(bonus: BonusActivity[]) {
  return bonus.filter(
    (activity) =>
      activity.group === null || (activity.group !== null && activity.state !== SUBMISSION_OPEN),
  );
}

/**
 * Load activities
 *
 * @param refresh true when we want to always load from BE
 */
export const loadBonusActivities = (refresh = false, userId = '') =>
  createAction({
    endpoint: endpointWithUserId('/activities/bonus', userId),
    method: HTTP_GET,
    types: [LOAD_BONUS_ACTIVITIES, LOAD_BONUS_ACTIVITIES_SUCCESS, LOAD_BONUS_ACTIVITIES_FAILURE],
    bailout: (state: StoreState) => {
      return !refresh && isLoaded(state);
    },
  });

export const dismissProminentBooklets = (bookletIds: Array<string>) =>
  createAction({
    endpoint: '/activities/bonus/dismiss-booklets',
    method: HTTP_PUT,
    types: [
      {
        type: DISMISS_PROMINENT_BOOKLETS,
        payload: { bookletIds },
      },
      DISMISS_PROMINENT_BOOKLETS_SUCCESS,
      DISMISS_PROMINENT_BOOKLETS_ERROR,
    ],
    headers: { [HTTP_CONTENT_TYPE_HEADER]: HTTP_CONTENT_TYPE_JSON },
    body: JSON.stringify({ payload: { bookletIds } }),
  });

export const resetBonusActivities = () => {
  return {
    type: RESET_BONUS_ACTIVITIES,
  };
};

export const deleteBonusActivity = (activityId: number, year: number): ThunkAction => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { activeUserId } = getState().dashboard;
    dispatch(
      createAction({
        endpoint: endpointWithUserId(
          `/activities/bonus/delete/${activityId}/${year}`,
          activeUserId,
        ),
        method: HTTP_DELETE,
        types: [
          DELETE_BONUS_ACTIVITY,
          DELETE_BONUS_ACTIVITY_SUCCESS,
          DELETE_BONUS_ACTIVITY_FAILURE,
        ],
      }),
    );
  };
};

export const resetDeleteActivity = () => {
  return {
    type: DELETE_BONUS_ACTIVITY_RESET,
  };
};

export const loadBonusAttachments = (activity: BonusActivity): ThunkAction => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { activeUserId } = getState().dashboard;
    dispatch(
      createAction({
        endpoint: endpointWithUserId(
          `/activities/bonus/attachment/meta/${activity.year}/${activity.id}`,
          activeUserId,
        ),
        method: HTTP_GET,
        types: [
          LOAD_BONUS_ACTIVITY_ATTACHMENT,
          LOAD_BONUS_ACTIVITY_ATTACHMENT_SUCCESS,
          LOAD_BONUS_ACTIVITY_ATTACHMENT_FAILURE,
        ],
      }),
    );
  };
};
